/**
 * **Susunan combo / paket** untuk satu produk ber-`product_kind = 'combo'`.
 *
 * ### ⛔⛔ Kenapa layar ini akhirnya dibuat
 * Mesinnya sudah lengkap sejak lama: katalog menurunkan grup & opsinya (`pos-core/catalog.ts`),
 * pesanan merakit baris komponennya (`order.ts`), dan `min_select` DITEGAKKAN saat kirim. Yang tak
 * ada cuma tempat menyusunnya — jadi POS **bisa menjual combo yang tak bisa dibuat siapa pun**.
 * Satu-satunya jalan membuatnya: SQL tangan. (Audit 2026-08-06.)
 *
 * ### ⭐ Bentuknya: GRUP berisi OPSI
 * | Lapis | Contoh | Yang ditegakkan |
 * |---|---|---|
 * | grup | "Pilih minuman" | `min_select` / `max_select` |
 * | opsi | Es Teh · Jus Jeruk (+5.000) | `upcharge`, `is_default` |
 *
 * ⚠️ **Komponen TETAP = grup dengan satu opsi, `min = max = 1`.** Sengaja tak ada bendera "fixed vs
 * choice" (lihat registry): satu mekanisme seragam lebih sedikit kemungkinan salahnya daripada dua
 * yang mirip — kasir, KDS, dan mesin harga cukup memahami satu bentuk.
 *
 * ⚠️⚠️ **`upcharge` = SELISIH harga, bukan harga komponen.** Harga komponen tetap nol di tagihan;
 * yang bertambah adalah harga induknya. Menyimpannya sebagai harga komponen membuat pajak terhitung
 * di dua tempat — dan selisihnya baru terlihat saat laporan pajak disusun.
 *
 * ### ⭐⭐ Diperbaiki 2026-08-06 sesudah membaca ARCON4 (aplikasi reservation Imron)
 * Di sana set menu ditampilkan sebagai **stepper bernama urut** — Appetizer → Main → Dessert — dan
 * itu bukan hiasan: **urutan grup ADALAH urutan hidangan**. Bukti dari VTEC: `SET MENU A` punya
 * **10 grup / 38 komponen** (§8d). Daftar tanpa urutan pada skala itu tak bisa disusun siapa pun,
 * dan yang tak bisa diurutkan akan muncul acak di layar kasir.
 *
 * Yang ditambahkan — semuanya sudah didukung server sejak awal, cuma tak pernah dipakai layar:
 * `sort_order` (naik/turun) · sunting grup (nama, min, max) · jalan pintas **komponen tetap** ·
 * pratinjau tampilan kasir.
 *
 * ⛔ Jalan pintas "komponen tetap" **tidak** menambah mekanisme kedua — ia tetap membuat grup
 * `min=max=1` berisi satu opsi, persis yang §8d putuskan. Yang dipendekkan cuma jaraknya dengan
 * cara orang berpikir (*"nasi selalu ikut"*), bukan bentuk datanya.
 */
import {
  Alert, Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table,
  Tag, Typography, message,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Product } from '../api/client';
import { canWrite } from '../api/session';

/** Daftar opsi — milik TENANT, dipakai bersama antar set menu. */
interface ComboGroup {
  readonly id: string;
  readonly code: string;
  readonly name: string;
}

/** Grup ini dipakai set menu itu, sebanyak sekian, di urutan sekian. */
interface ComboLink {
  readonly id: string;
  readonly combo_product_id: string;
  readonly group_id: string;
  readonly min_select: number;
  readonly max_select: number;
  readonly sort_order: number;
}

interface ComboOption {
  readonly id: string;
  readonly group_id: string;
  readonly component_product_id: string;
  readonly default_qty: string;
  readonly upcharge: string;
  readonly is_default: boolean;
  readonly sort_order: number;
}

function rupiah(v: string): string {
  return `Rp ${(v.split('.')[0] ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
}

export function ProductCombo({ productId }: { readonly productId: string }) {
  const { t } = useTranslation();
  const boleh = canWrite();
  const [link, setLink] = useState<readonly ComboLink[]>([]);
  /** SELURUH grup tenant — bahan pemilih "pakai grup yang sudah ada". */
  const [grup, setGrup] = useState<readonly ComboGroup[]>([]);
  /** Berapa set menu memakai tiap grup — yang harus dilihat orang SEBELUM menyunting. */
  const [pemakai, setPemakai] = useState<Readonly<Record<string, number>>>({});
  const [opsi, setOpsi] = useState<readonly ComboOption[]>([]);
  const [produk, setProduk] = useState<readonly Product[]>([]);
  const [formGrup] = Form.useForm();
  /** Grup yang sedang disunting. `null` = tak ada. */
  const [sunting, setSunting] = useState<ComboLink | null>(null);
  const [formSunting] = Form.useForm();

  const muat = useCallback(async () => {
    const [l, g, semuaLink] = await Promise.all([
      api.listMaster<ComboLink>('combo-product-groups', { combo_product_id: productId }),
      api.listMaster<ComboGroup>('combo-groups'),
      /* ⚠️ SELURUH link, bukan cuma milik produk ini: yang mau dijawab "grup ini dipakai berapa
         set menu", dan jawabannya justru ada di luar produk yang sedang dibuka. */
      api.listMaster<ComboLink>('combo-product-groups'),
    ]);
    setLink(l);
    setGrup(g);
    const hitung: Record<string, number> = {};
    for (const x of semuaLink) hitung[x.group_id] = (hitung[x.group_id] ?? 0) + 1;
    setPemakai(hitung);

    /*
     * ⚠️ Opsi diambil per-grup: `combo_option` tak punya kolom yang menunjuk produk induk, jadi tak
     * ada saringan yang bisa mengambil "semua opsi set menu ini" sekaligus. Yang ditarik hanya grup
     * yang benar-benar dipakai produk ini — bukan seluruh tabel.
     */
    const isi = await Promise.all(
      l.map((x) => api.listMaster<ComboOption>('combo-options', { group_id: x.group_id })),
    );
    setOpsi(isi.flat());
  }, [productId]);

  useEffect(() => { void muat().catch(() => undefined); }, [muat]);
  useEffect(() => {
    /* ⛔ Produk yang bisa jadi komponen: `simple` saja — combo di dalam combo tak dimodelkan. */
    void api.listMaster<Product>('products').then((p) => {
      setProduk(p.filter((x) => x.product_kind !== 'combo'));
    }).catch(() => undefined);
  }, []);

  const jalankan = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await muat();
    } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    }
  };

  const namaProduk = (id: string) => produk.find((p) => p.id === id)?.name ?? id.slice(0, 8);

  /**
   * ⭐⭐ **Menukar urutan dua grup** — dan urutan grup ADALAH urutan hidangan (ARCON4 menampilkannya
   * sebagai stepper: Appetizer → Main → Dessert).
   *
   * ⚠️ Yang ditukar **nilai `sort_order` keduanya**, bukan "geser satu lalu biarkan sisanya":
   * menggeser satu saja membuat dua grup berbagi angka yang sama, dan urutan di antara keduanya
   * lalu ditentukan hal yang tak seorang pun kendalikan.
   */
  const pindah = (l: ComboLink, arah: -1 | 1) => {
    const i = urut.findIndex((x) => x.id === l.id);
    const lawan = urut[i + arah];
    if (lawan === undefined) return;
    void jalankan(async () => {
      await api.updateMaster('combo-product-groups', l.id, { sortOrder: lawan.sort_order });
      await api.updateMaster('combo-product-groups', lawan.id, { sortOrder: l.sort_order });
    });
  };

  /**
   * ⭐ **Komponen tetap** — "nasi selalu ikut". Satu ketukan, TIGA penulisan: grup, opsinya, dan
   * link ber-`min=max=1`.
   *
   * ⛔ BUKAN mekanisme kedua (§8d): bentuk datanya sama persis dengan langkah pilihan. Yang
   * dipendekkan jaraknya dengan cara orang berpikir.
   */
  const tambahTetap = (v: Record<string, unknown>) => {
    const p = produk.find((x) => x.id === v['componentProductId']);
    if (p === undefined) return;
    void jalankan(async () => {
      const g = await api.createMaster('combo-groups', {
        /* ⚠️ Kode grup kini UNIK SE-TENANT (ia dipakai bersama) ⇒ diturunkan dari kode produknya. */
        code: `FIX-${p.code}`.slice(0, 40).toUpperCase(),
        name: p.name,
      });
      const gid = String((g as { id?: unknown }).id ?? '');
      await api.createMaster('combo-options', {
        groupId: gid, componentProductId: p.id,
        defaultQty: String(v['defaultQty'] ?? 1), upcharge: '0.0000',
      });
      await api.createMaster('combo-product-groups', {
        comboProductId: productId, groupId: gid, minSelect: 1, maxSelect: 1, sortOrder: link.length,
      });
    });
  };

  const grupDari = (l: ComboLink) => grup.find((g) => g.id === l.group_id);

  /** Urut sesuai `sort_order` LINK — inilah urutan stepper yang dilihat kasir. */
  const urut = [...link].sort((a, b) => a.sort_order - b.sort_order);

  /**
   * ⭐ Langkah yang efeknya "selalu ikut": satu opsi, `min = max = 1`.
   *
   * ⚠️ DIHITUNG dari bentuknya, bukan dari bendera — memang tak ada benderanya (§8d).
   */
  const tetap = (l: ComboLink): boolean =>
    l.min_select === 1 && l.max_select === 1 &&
    opsi.filter((o) => o.group_id === l.group_id).length === 1;

  return (
    <Card size="small" title={t('combo.title')} style={{ marginTop: 16 }} data-testid="combo-kartu">
      <Alert type="info" showIcon style={{ marginBottom: 12 }} title={t('combo.hint')} />

      {link.length === 0 && (
        /*
         * ⛔ Bukan "belum ada data": produk ber-jenis combo TANPA grup akan terjual sebagai paket
         * kosong — tamu membayar harga paket dan dapurnya tak menerima satu komponen pun.
         */
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
               title={t('combo.empty')} data-testid="combo-kosong" />
      )}

      {urut.map((l, idx) => {
        const g = grupDari(l);
        const kode = g?.code ?? l.group_id.slice(0, 8);
        const isi = opsi.filter((o) => o.group_id === l.group_id)
          .sort((a, b) => a.sort_order - b.sort_order);
        const dipakai = pemakai[l.group_id] ?? 1;
        return (
          <Card
            key={l.id}
            size="small"
            style={{ marginBottom: 12 }}
            data-testid={`combo-grup-${kode}`}
            title={
              <Space wrap>
                {/* ⭐ Nomor langkah — inilah urutan yang dilihat kasir. */}
                <Typography.Text type="secondary">{idx + 1}.</Typography.Text>
                <b>{kode}</b>
                <span>{g?.name}</span>
                {tetap(l)
                  ? <Tag color="blue" data-testid={`combo-tetap-${kode}`}>{t('combo.fixed')}</Tag>
                  : (
                    <Typography.Text type="secondary">
                      {l.min_select === l.max_select
                        ? t('combo.exactly', { n: l.min_select })
                        : t('combo.range', { min: l.min_select, max: l.max_select })}
                    </Typography.Text>
                  )}
                {/*
                  ⭐⭐⭐ **Peringatan pakai-bersama.** Menyunting OPSI grup ini mengubah SEMUA set
                  menu yang memakainya — dan itu justru gunanya. Yang berbahaya melakukannya tanpa
                  tahu: satu tambahan di sini bisa muncul di sepuluh paket sekaligus.
                */}
                {dipakai > 1 && (
                  <Tag color="orange" data-testid={`combo-dipakai-${kode}`}>
                    {t('combo.sharedBy', { n: dipakai })}
                  </Tag>
                )}
              </Space>
            }
            extra={
              <Space size={4}>
                {/* ⚠️ Naik/turun, bukan seret: 10 langkah di layar sempit lebih sering salah seret
                    daripada tepat, dan salah seret mengubah urutan hidangan. */}
                <Button size="small" disabled={!boleh || idx === 0}
                        data-testid={`combo-naik-${kode}`}
                        onClick={() => { pindah(l, -1); }}>↑</Button>
                <Button size="small" disabled={!boleh || idx === urut.length - 1}
                        data-testid={`combo-turun-${kode}`}
                        onClick={() => { pindah(l, 1); }}>↓</Button>
                <Button size="small" disabled={!boleh}
                        data-testid={`combo-sunting-${kode}`}
                        onClick={() => {
                          setSunting(l);
                          formSunting.setFieldsValue({
                            minSelect: l.min_select, maxSelect: l.max_select,
                          });
                        }}>
                  {t('combo.editGroup')}
                </Button>
                {/*
                  ⛔ "Lepas", bukan "Hapus": yang dicabut LINK-nya; grupnya tetap hidup untuk set
                  menu lain. Menyebutnya hapus membuat orang mengira ia merusak paket orang lain —
                  lalu ia tak berani merapikan paketnya sendiri.
                */}
                <Popconfirm
                  title={t('combo.detachGroup')}
                  okText={t('crud.save')}
                  cancelText={t('crud.cancel')}
                  onConfirm={() => void jalankan(() =>
                    api.deleteMaster('combo-product-groups', l.id))}
                >
                  <Button size="small" danger disabled={!boleh}
                          data-testid={`combo-lepas-${kode}`}>
                    {t('combo.detach')}
                  </Button>
                </Popconfirm>
              </Space>
            }
          >
            <Table<ComboOption>
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={[...isi]}
              locale={{ emptyText: t('combo.noOption') }}
              columns={[
                { title: t('combo.component'), render: (_: unknown, o: ComboOption) => namaProduk(o.component_product_id) },
                {
                  /*
                   * ⭐⭐⭐ **Jumlah per OPSI** — inilah yang menjawab "pilih ayam dapat 2, beef
                   * dapat 1" tanpa menyerahkannya ke ingatan pelayan (kasus Imron 2026-08-06).
                   */
                  title: t('combo.qtyPerOption'),
                  width: 110,
                  render: (_: unknown, o: ComboOption) => o.default_qty.replace(/\.?0+$/, ''),
                },
                {
                  title: t('combo.upcharge'),
                  width: 130,
                  render: (_: unknown, o: ComboOption) => (o.upcharge.startsWith('0.') ? '—' : rupiah(o.upcharge)),
                },
                {
                  title: t('combo.default'),
                  width: 110,
                  render: (_: unknown, o: ComboOption) => (
                    <Switch
                      size="small"
                      checked={o.is_default}
                      disabled={!boleh}
                      data-testid={`combo-bawaan-${o.id}`}
                      onChange={(v) => void jalankan(() => api.updateMaster('combo-options', o.id, { isDefault: v }))}
                    />
                  ),
                },
                {
                  title: '',
                  width: 90,
                  render: (_: unknown, o: ComboOption) => (
                    <Button size="small" danger disabled={!boleh}
                            onClick={() => void jalankan(() => api.deleteMaster('combo-options', o.id))}>
                      {t('floor.delete')}
                    </Button>
                  ),
                },
              ]}
            />

            {boleh && (
              <Form
                layout="inline"
                style={{ marginTop: 8 }}
                onFinish={(v: Record<string, unknown>) => {
                  void jalankan(async () => {
                    await api.createMaster('combo-options', {
                      groupId: l.group_id,
                      componentProductId: v['componentProductId'],
                      /* ⚠️ Qty & upcharge dikirim STRING desimal — float di jalur uang tak pernah. */
                      defaultQty: String(v['defaultQty'] ?? 1),
                      upcharge: `${String(Math.round(Number(v['upcharge'] ?? 0)))}.0000`,
                    });
                  });
                }}
              >
                <Form.Item name="componentProductId" rules={[{ required: true }]}>
                  <Select
                    showSearch
                    style={{ width: 240 }}
                    placeholder={t('combo.component')}
                    optionFilterProp="label"
                    data-testid={`combo-pilih-produk-${kode}`}
                    /*
                     * ⛔ Yang SUDAH ada di grup ini disembunyikan. DDL memang menolaknya sejak
                     * migrasi 0057 — tapi menawarkan pilihan yang pasti gagal itu menyuruh orang
                     * menabrak tembok lalu membaca kode galat. ⚠️ Ini kesopanan layar, BUKAN
                     * kontrol: kontrolnya tetap indeks unik, karena layar bisa dilewati.
                     */
                    options={produk
                      .filter((p) => !isi.some((o) => o.component_product_id === p.id))
                      .map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` }))}
                  />
                </Form.Item>
                <Form.Item name="defaultQty" initialValue={1} tooltip={t('combo.qtyPerOptionHint')}>
                  <InputNumber min={1} style={{ width: 90 }} addonAfter="×" />
                </Form.Item>
                <Form.Item name="upcharge" initialValue={0}>
                  <InputNumber min={0} style={{ width: 130 }} addonBefore="+Rp" />
                </Form.Item>
                <Form.Item>
                  <Button htmlType="submit" data-testid={`combo-tambah-opsi-${kode}`}>
                    {t('combo.addOption')}
                  </Button>
                </Form.Item>
              </Form>
            )}
          </Card>
        );
      })}

      {/*
        ⭐⭐⭐ **Pakai grup yang SUDAH ADA** — jalur yang menjawab 67% duplikasi Arena.
        Ditaruh SEBELUM "buat grup baru" dengan sengaja: inilah yang seharusnya dicoba dulu.
        "Pilihan udang" biasanya sudah ada; yang beda cuma berapa yang didapat.
      */}
      {boleh && grup.filter((g) => !link.some((x) => x.group_id === g.id)).length > 0 && (
        <Form
          layout="inline"
          style={{ marginTop: 12 }}
          onFinish={(v: Record<string, unknown>) => {
            void jalankan(() => api.createMaster('combo-product-groups', {
              comboProductId: productId,
              groupId: v['groupId'],
              minSelect: Number(v['minSelect'] ?? 1),
              maxSelect: Number(v['maxSelect'] ?? 1),
              sortOrder: link.length,
            }));
          }}
        >
          <Form.Item name="groupId" rules={[{ required: true }]}>
            <Select
              showSearch
              style={{ width: 300 }}
              placeholder={t('combo.reusePick')}
              optionFilterProp="label"
              data-testid="combo-pakai-grup"
              /* ⛔ Yang sudah dipakai set menu INI disembunyikan — satu grup hanya boleh menempel
                 sekali (unik di DDL), dan menawarkannya lagi cuma menghasilkan galat. */
              options={grup.filter((g) => !link.some((x) => x.group_id === g.id))
                .map((g) => ({
                  value: g.id,
                  label: `${g.code} · ${g.name}${(pemakai[g.id] ?? 0) > 0
                    ? ` (${t('combo.sharedBy', { n: pemakai[g.id] ?? 0 })})` : ''}`,
                }))}
            />
          </Form.Item>
          <Form.Item name="minSelect" initialValue={1} label={t('combo.min')}>
            <InputNumber min={0} style={{ width: 70 }} data-testid="combo-pakai-min" />
          </Form.Item>
          <Form.Item name="maxSelect" initialValue={1} label={t('combo.max')}>
            <InputNumber min={1} style={{ width: 70 }} data-testid="combo-pakai-max" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" data-testid="combo-tambah-pakai">
              {t('combo.reuseAdd')}
            </Button>
          </Form.Item>
        </Form>
      )}

      {boleh && (
        <Form
          form={formGrup}
          layout="inline"
          onFinish={(v: Record<string, unknown>) => {
            void jalankan(async () => {
              const g = await api.createMaster('combo-groups', {
                code: String(v['code']).toUpperCase(),
                name: v['name'],
              });
              await api.createMaster('combo-product-groups', {
                comboProductId: productId,
                groupId: String((g as { id?: unknown }).id ?? ''),
                minSelect: Number(v['minSelect'] ?? 1),
                maxSelect: Number(v['maxSelect'] ?? 1),
                sortOrder: link.length,
              });
              formGrup.resetFields();
            });
          }}
        >
          <Form.Item name="code" rules={[{ required: true, pattern: /^[A-Za-z0-9_-]+$/ }]}>
            <Input placeholder={t('combo.groupCode')} style={{ width: 130 }} data-testid="combo-grup-kode" />
          </Form.Item>
          <Form.Item name="name" rules={[{ required: true, min: 2 }]}>
            <Input placeholder={t('combo.groupName')} style={{ width: 200 }} data-testid="combo-grup-nama" />
          </Form.Item>
          {/*
            ⚠️ `min` & `max` bukan hiasan: `min_select` DITEGAKKAN server saat kirim ke dapur —
            paket yang belum lengkap ditolak, bukan diterima lalu jadi masalah dapur.
          */}
          <Form.Item name="minSelect" initialValue={1} label={t('combo.min')}>
            <InputNumber min={0} style={{ width: 70 }} data-testid="combo-min" />
          </Form.Item>
          <Form.Item name="maxSelect" initialValue={1} label={t('combo.max')}>
            <InputNumber min={1} style={{ width: 70 }} data-testid="combo-max" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" data-testid="combo-tambah-grup">
              {t('combo.addGroup')}
            </Button>
          </Form.Item>
        </Form>
      )}

      {/*
        ⭐⭐ **Jalan pintas komponen TETAP.** Sebelum ini, menambah "nasi selalu ikut" berarti
        mengisi formulir grup, mengerti bahwa `min=max=1` artinya wajib, lalu mengisi formulir opsi.
        Tiga langkah untuk satu maksud yang paling sering dipakai — dan yang bertele-tele akan
        diakali: orang membuat produk paket TANPA komponen wajibnya, lalu dapur yang menebak.
      */}
      {boleh && (
        <Form
          layout="inline"
          style={{ marginTop: 12 }}
          onFinish={(v: Record<string, unknown>) => { tambahTetap(v); }}
        >
          <Form.Item name="componentProductId" rules={[{ required: true }]}>
            <Select
              showSearch
              style={{ width: 240 }}
              placeholder={t('combo.fixedPick')}
              optionFilterProp="label"
              data-testid="combo-tetap-produk"
              options={produk.map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` }))}
            />
          </Form.Item>
          <Form.Item name="defaultQty" initialValue={1}>
            <InputNumber min={1} style={{ width: 80 }} placeholder={t('combo.qty')} />
          </Form.Item>
          <Form.Item>
            <Button htmlType="submit" data-testid="combo-tambah-tetap">
              {t('combo.addFixed')}
            </Button>
          </Form.Item>
        </Form>
      )}

      {/*
        ⭐⭐⭐ **Pratinjau: beginilah nanti di layar kasir.**
        Yang paling sering salah saat menyusun set menu bukan datanya, melainkan bayangan orang
        tentang hasilnya — "pilih 1 dari 1" terbaca sebagai pilihan padahal ia keharusan, dan grup
        tanpa urutan terbaca urut padahal tidak.
      */}
      {urut.length > 0 && (
        <Card size="small" style={{ marginTop: 16 }} title={t('combo.preview')}
              data-testid="combo-pratinjau">
          <ol style={{ margin: 0, paddingInlineStart: 20 }}>
            {urut.map((l) => {
              const g = grupDari(l);
              const isi = opsi.filter((o) => o.group_id === l.group_id);
              return (
                <li key={l.id} style={{ marginBottom: 4 }}>
                  <b>{g?.name}</b>{' '}
                  {tetap(l)
                    ? <Typography.Text type="secondary">{t('combo.previewFixed', {
                        item: namaProduk(isi[0]?.component_product_id ?? ''),
                      })}</Typography.Text>
                    : <Typography.Text type="secondary">{t('combo.previewChoice', {
                        n: l.min_select === l.max_select
                          ? String(l.min_select)
                          : `${String(l.min_select)}\u2013${String(l.max_select)}`,
                        total: isi.length,
                      })}</Typography.Text>}
                  {/* ⛔ Langkah pilihan yang opsinya KURANG dari min: kasir tak akan pernah bisa
                      memenuhinya, dan paketnya ditolak saat kirim ke dapur — di depan tamu. */}
                  {!tetap(l) && isi.length < l.min_select && (
                    <Tag color="red" style={{ marginInlineStart: 8 }}
                         data-testid={`combo-mustahil-${g?.code ?? ''}`}>
                      {t('combo.impossible')}
                    </Tag>
                  )}
                </li>
              );
            })}
          </ol>
        </Card>
      )}

      <Modal
        open={sunting !== null}
        title={t('combo.editGroup')}
        okText={t('crud.save')}
        cancelText={t('crud.cancel')}
        onCancel={() => { setSunting(null); }}
        onOk={() => {
          void formSunting.validateFields().then((v: Record<string, unknown>) => {
            const l = sunting;
            if (l === null) return;
            setSunting(null);
            /* ⭐ Yang disunting LINK-nya — jumlah & urutan milik set menu ini saja, jadi mengubahnya
               tak menyentuh set menu lain yang memakai grup yang sama. */
            void jalankan(() => api.updateMaster('combo-product-groups', l.id, {
              minSelect: Number(v['minSelect']),
              maxSelect: Number(v['maxSelect']),
            }));
          });
        }}
      >
        <Form form={formSunting} layout="vertical">
          <Space>
            <Form.Item name="minSelect" label={t('combo.min')}>
              <InputNumber min={0} style={{ width: 90 }} data-testid="combo-sunting-min" />
            </Form.Item>
            <Form.Item name="maxSelect" label={t('combo.max')}>
              <InputNumber min={1} style={{ width: 90 }} data-testid="combo-sunting-max" />
            </Form.Item>
          </Space>
          {/* ⭐ Menyebut bahwa ini TIDAK menyentuh set menu lain — tanpa itu orang takut menyunting
              grup bersama, lalu menyalinnya, dan kembali ke duplikasi yang kita hindari. */}
          <Alert type="info" showIcon title={t('combo.editLinkHint')} />
        </Form>
      </Modal>
    </Card>
  );
}
